import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  periodsOptions, periodDetailOptions,
  adminPeriodsOptions, adminPeriodDetailOptions,
  adminFeesOptions, adminUsersOptions,
} from './queries';
import type {
  ExpenseItemResponse, SubmissionResponse, FeeConfigResponse, UserResponse,
} from './types';

// All query-key references go through the queryOptions() factories from queries.ts —
// keeps cache-shape in sync if a factory ever adds params (R5).

export function useFinalizeSubmission(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`periods/${periodId}/submissions/finalize`).json<SubmissionResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: periodsOptions().queryKey });
      void qc.invalidateQueries({ queryKey: periodDetailOptions(periodId).queryKey });
    },
  });
}

export function useUpsertDraft(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`periods/${periodId}/submissions`).json<SubmissionResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: periodDetailOptions(periodId).queryKey });
    },
  });
}

export function useCreateExpenseItem(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { submissionId: number; label: string; amountCents: number; subInvoiceId?: number | null }) =>
      api.post('expense-items', { json: body }).json<ExpenseItemResponse>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: periodDetailOptions(periodId).queryKey }),
  });
}

export function useDeleteExpenseItem(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`expense-items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: periodDetailOptions(periodId).queryKey }),
  });
}

export function useUploadSubInvoice(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, file }: { submissionId: number; file: File }) => {
      const fd = new FormData();
      fd.append('submissionId', String(submissionId));
      fd.append('file', file);
      return api.post('sub-invoices', { body: fd }).json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: periodDetailOptions(periodId).queryKey }),
  });
}

export function useToggleFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`admin/fees/${id}/toggle`).json<FeeConfigResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminFeesOptions().queryKey });
      void qc.invalidateQueries({ queryKey: adminPeriodsOptions().queryKey });
    },
  });
}

export function useCreateFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      type: 'FIXED' | 'PERCENT';
      name: string;
      description?: string;
      amountCents?: number;
      rateBasisPoints?: number;
      sortOrder?: number;
    }) => api.post('admin/fees', { json: body }).json<FeeConfigResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminFeesOptions().queryKey });
      void qc.invalidateQueries({ queryKey: adminPeriodsOptions().queryKey });
    },
  });
}

export function useUpdateFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: {
      id: number;
      body: {
        type?: 'FIXED' | 'PERCENT';
        name?: string;
        description?: string;
        amountCents?: number;
        rateBasisPoints?: number;
      };
    }) => api.patch(`admin/fees/${id}`, { json: body }).json<FeeConfigResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminFeesOptions().queryKey });
      void qc.invalidateQueries({ queryKey: adminPeriodsOptions().queryKey });
    },
  });
}

export function useDeleteFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`admin/fees/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminFeesOptions().queryKey });
      void qc.invalidateQueries({ queryKey: adminPeriodsOptions().queryKey });
    },
  });
}

export function useFinalizeMaster(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feeConfigEtag: string | null) =>
      api.post(`admin/periods/${periodId}/finalize-master`, {
        json: feeConfigEtag ? { feeConfigEtag } : {},
      }).json(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminPeriodsOptions().queryKey });
      void qc.invalidateQueries({ queryKey: adminPeriodDetailOptions(periodId).queryKey });
    },
  });
}

export function useVoidMaster(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`admin/periods/${periodId}/void-master`).json(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminPeriodsOptions().queryKey });
      void qc.invalidateQueries({ queryKey: adminPeriodDetailOptions(periodId).queryKey });
    },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`admin/users/${id}/deactivate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminUsersOptions('ACTIVE').queryKey });
      void qc.invalidateQueries({ queryKey: adminUsersOptions('INACTIVE').queryKey });
    },
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`admin/users/${id}/reactivate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminUsersOptions('ACTIVE').queryKey });
      void qc.invalidateQueries({ queryKey: adminUsersOptions('INACTIVE').queryKey });
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string; legalName: string; role: 'EMPLOYEE' | 'ADMIN';
      baseSalaryCents: number; invoicePrefix: string; initialPassword: string;
      invoiceCounterStart?: number;
      billingAddress?: string; taxId?: string; iban?: string;
    }) => api.post('admin/users', { json: body }).json<UserResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminUsersOptions('ACTIVE').queryKey });
      void qc.invalidateQueries({ queryKey: adminUsersOptions('INACTIVE').queryKey });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: {
      id: number;
      body: {
        legalName?: string; role?: 'EMPLOYEE' | 'ADMIN';
        baseSalaryCents?: number; invoicePrefix?: string;
        billingAddress?: string; taxId?: string; iban?: string;
      };
    }) => api.patch(`admin/users/${id}`, { json: body }).json<UserResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminUsersOptions('ACTIVE').queryKey });
      void qc.invalidateQueries({ queryKey: adminUsersOptions('INACTIVE').queryKey });
      // /me may have changed (admin editing themselves)
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: number; newPassword: string }) =>
      api.post(`admin/users/${id}/reset-password`, { json: { newPassword } }),
  });
}
